import lodash from 'lodash'
const { merge } = lodash
import { IsOf } from '../../../guards'
import { Apply, CortezaID, NoID } from '../../../cast'
import { read } from 'fs'

export const FieldNameValidator = /^[A-Za-z][0-9A-Za-z_-]*[A-Za-z0-9]$/

const unsortableFieldKinds = ['File', 'Geometry']
const unsortableSysFields = ['recordID']

const unfilterableFieldKinds = ['File', 'Geometry']
const nonQueryableFieldKinds = ['Number', 'Record', 'User', 'Bool', 'DateTime', 'File', 'Geometry']
const nonQueryableFieldNames = ['recordID']

type fieldEncoding = null | { omit: true } | { ident: string }

export interface Capabilities {
  configurable: true;
  multi: boolean;
  writable: boolean;
  required: boolean;
  private: boolean;
}

export interface Options {
  description: {
    view: string;
    edit: string | undefined;
  };
  hint: {
    view: string;
    edit: string | undefined;
  };
  __mirror: {
    corece: string;
    from: string;
    on: string;
    split: string;
  };
  readonly: boolean;
}

export const defaultOptions = (): Readonly<Options> => Object.freeze({
  description: {
    view: '',
    edit: undefined,
  },
  hint: {
    view: '',
    edit: undefined,
  },
  __mirror: {
    corece: '',
    from: '',
    on: '',
    split: '',
  },
  readonly: false,
})

interface Config {
  dal: {
    encodingStrategy: fieldEncoding;
  };

  privacy: {
    sensitivityLevelID: string;
    usageDisclosure: string;
  };

  recordRevisions: {
    enabled: boolean;
  };
}

export interface Expressions {
  value?: string;

  sanitizers?: Array<string>;

  validators?: Array<Validator>;
  disableDefaultValidators?: boolean;

  formatters?: Array<string>;
  disableDefaultFormatters?: boolean;
}

interface Validator {
  validatorID: string;
  test: string;
  error: string;
}

interface DefaultValue {
  name?: string;
  value: string;
}

export class ModuleField {
  public fieldID = NoID
  public name = ''
  public kind = ''
  public label = ''

  public defaultValue: Array<DefaultValue> = []
  public maxLength = 0

  public isRequired = false
  public isMulti = false
  public isSystem = false
  public isSortable = true
  public isFilterable = true
  public isQueryable = true
  public options: Options = { ...defaultOptions() }
  public expressions: Expressions = {}

  public config: Partial<Config> = {
    dal: {
      encodingStrategy: null,
    },

    privacy: {
      sensitivityLevelID: NoID,
      usageDisclosure: '',
    },

    recordRevisions: {
      enabled: false,
    },
  }

  public canUpdateRecordValue = false
  public canReadRecordValue = false

  constructor(f?: Partial<ModuleField>) {
    this.apply(f)
  }

  applyOptions(o?: Partial<Options>): void {
    if (!o) return

    if (o.description) {
      this.options.description = {
        ...this.options.description,
        ...o.description,
      }

      this.options.description.edit = this.options.description.edit || undefined
    }

    if (o.hint) {
      this.options.hint = {
        ...this.options.hint,
        ...o.hint,
      }

      this.options.hint.edit = this.options.hint.edit || undefined
    }

    if (o.__mirror){
      this.options.__mirror = {
        ...this.options.__mirror,
        ...o.__mirror,
      }

      this.options.__mirror.from = this.options.__mirror.from || ''
      this.options.__mirror.on = this.options.__mirror.on || ''
      this.options.__mirror.split = this.options.__mirror.split || ''
      this.options.__mirror.corece = this.options.__mirror.corece || ''
    }

    if (o.readonly !== undefined) {
      this.options.readonly = o.readonly
    }
  }

  clone(): ModuleField {
    return new ModuleField(JSON.parse(JSON.stringify(this)))
  }

  public apply(f?: Partial<ModuleField>): void {
    if (!f) return

    Apply(this, f, CortezaID, 'fieldID')
    Apply(this, f, String, 'name', 'label', 'kind')
    Apply(this, f, Number, 'maxLength')
    Apply(this, f, Boolean, 'isRequired', 'isMulti', 'isSystem')

    // Make sure field is align with it's capabilities
    if (!this.cap.multi) this.isMulti = false
    if (!this.cap.required) this.isRequired = false

    // Check if kind sortable
    if (unsortableFieldKinds.includes(this.kind)) {
      this.isSortable = false
    }

    if (unfilterableFieldKinds.includes(this.kind)) {
      this.isFilterable = false
    }

    if (nonQueryableFieldKinds.includes(this.kind)) {
      this.isQueryable = false
    }

    if (nonQueryableFieldNames.includes(this.name)) {
      this.isQueryable = false
    }

    if (f.defaultValue && Array.isArray(f.defaultValue)) {
      /**
       * Converting default value into proper format
       * so we can use it without conversion
       * false boolean values are represented only by the name, in all other cases the value is also present
       */
      this.defaultValue = f.defaultValue.filter(({ name, value }) => name !== undefined || (value !== undefined && value !== null))
    }

    if (this.isSystem) {
      this.canUpdateRecordValue = true
      this.canReadRecordValue = true

      if (unsortableSysFields.includes(this.name)) {
        this.isSortable = false
      }
    } else {
      Apply(this, f, Boolean, 'canUpdateRecordValue', 'canReadRecordValue')
    }

    if (IsOf(f, 'config')) {
      this.config = merge({}, this.config, f.config)
    }

    if (IsOf(f, 'kind')) {
      this.kind = f.kind
    }

    if (IsOf(f, 'expressions')) {
      this.expressions = f.expressions
    }
  }

  /**
   * Test field validity
   *
   * Expecting valid name
   */
  public get isValid(): boolean {
    return this.name.length > 0 && FieldNameValidator.test(this.name)
  }

  /**
   * Per module field type capabilities
   */
  public get cap(): Readonly<Capabilities> {
    return {
      configurable: true,
      multi: true,
      writable: true,
      required: true,
      private: true,
    }
  }

  /**
   * Returns resource ID
   */
  get resourceID(): string {
    return `${this.resourceType}:${this.fieldID}`
  }

  /**
   * Resource type
   */
  get resourceType(): string {
    return 'compose:module-field'
  }
}

export const Registry = new Map<string, typeof ModuleField>()
